import { trigger, transition, style, animate } from '@angular/animations';
import { Component, Input, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IIHCManagementExperience } from 'app/models/licenses/in-home-care/ihc-management-experience';
import { IStatus } from 'app/models/licenses/status';
import { AdministratorApplicationService } from 'app/services/license/in-home-care/administrator-application.service';

@Component({
  selector: 'management-experience',
  templateUrl: './management-experience.component.html',
  styleUrls: ['./management-experience.component.scss'],
  animations: [
    trigger('slideLeft', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(-100%)' }))
      ])
    ])
  ]
})
export class ManagementExperienceComponent {
  @Input() applicationId: number = 0;
  @Input() isChange: boolean = false;
  @Input() statuses: IStatus[] = [];
  @Input() isEditable: boolean = false;

  public includeArchived: boolean = false;
  public addingExperience: boolean = false;
  public newExperienceCreated: boolean = false;
  public experience: IIHCManagementExperience = {
    id: 0,
    applicationId: 0,
    company: '',
    phone: '',
    address: '',
    supervisor: '',
    jobTitle: '',
    managementDuties: '',
    from: '',
    to: '',
    isHealthcareField: null,
    healthcareField: '',
    mayContactEmployer: null,
    archived: false
  }
  public experiences: IIHCManagementExperience[] = [];
  public experienceForm = new FormGroup({
    company: new FormControl('', Validators.required),
    phone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    address: new FormControl('', Validators.required),
    supervisor: new FormControl('', Validators.required),
    jobTitle: new FormControl('', Validators.required),
    managementDuties: new FormControl('', Validators.required),
    from: new FormControl<Date>(null, Validators.required),
    to: new FormControl<Date>(null),
    isHealthcareField: new FormControl(null),
    healthcareField: new FormControl(''),
    mayContactEmployer: new FormControl(null)
  });

  public experienceColumns: string [] = ['company', 'jobTitle', 'phone', 'actions'];
  public experienceSource = new MatTableDataSource<IIHCManagementExperience>(this.experiences);
  @ViewChild('experiencePaginator', { static: false })
  set experiencePaginator(value: MatPaginator | undefined) {
    // Conditional views recreate these controls independently of data loading.
    this.experienceSource.paginator = value ?? null;
  }
  get experiencePaginator(): MatPaginator {
    return this.experienceSource.paginator!;
  }
  @ViewChild('experienceSort', { static: false })
  set experienceSort(value: MatSort | undefined) {
    // Conditional views recreate these controls independently of data loading.
    this.experienceSource.sort = value ?? null;
  }
  get experienceSort(): MatSort {
    return this.experienceSource.sort!;
  }

  public sharedService = inject(SharedService);
  public applicationService = inject(AdministratorApplicationService);

  ngOnInit(): void {
    this.getExperience(this.includeArchived);
  }

  ngOnDestroy(): void {
    if(this.addingExperience && !this.locked){
      this.saveExperience();
    }
  }

  getExperience(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    this.applicationService.getManagementExperiences(this.applicationId, includeArchived).subscribe({
      next: response => this.experiences = response,
      error: e => console.log('error getting management experience', e),
      complete: () => this.updateExperienceTable()
    })
  }

  updateExperienceTable(): void {
    this.experienceSource.data = this.experiences;
  }

  createExperience(): void {
    this.experience.applicationId = this.applicationId;
    this.applicationService.createManagementExperience(this.experience).subscribe({
      next: response => {
        this.experience = response;
        this.experiences.push(response);
      },
      error: e => {
        this.sharedService.toastr.error("Could Not Create Management Experience");
        console.log('error creating management experience', e);
      },
      complete: () => {
        this.toggleExperienceForm(true);
        this.newExperienceCreated = true;
      }
    });
  }

  getEmptyExperience(): IIHCManagementExperience {
    return {
      id: 0,
      applicationId: 0,
      company: '',
      phone: '',
      address: '',
      supervisor: '',
      jobTitle: '',
      managementDuties: '',
      from: '',
      to: '',
      isHealthcareField: null,
      healthcareField: '',
      mayContactEmployer: null,
      archived: false
    }
  }

  selectExperience(experience: IIHCManagementExperience): void {
    this.updateExperienceForm(experience);
    this.experience = experience;
    this.addingExperience = true;
  }

  toggleExperienceForm(adding: boolean): void {
    this.addingExperience = adding;
    this.updateExperienceForm(this.getEmptyExperience());
    if(!adding){
      if(this.newExperienceCreated){
        this.deleteExperience(this.experience.id);
      }
      else{
        this.updateExperienceTable();
        this.experience = this.getEmptyExperience();
      }
    }
  }

  updateExperience(): void {
    let form = this.experienceForm.value;
    this.experience.company = form.company;
    this.experience.phone = form.phone;
    this.experience.address = form.address;
    this.experience.supervisor = form.supervisor;
    this.experience.jobTitle = form.jobTitle;
    this.experience.managementDuties = form.managementDuties;
    this.experience.from = form.from?.toDateString();
    this.experience.to = form.to?.toDateString();
    this.experience.isHealthcareField = form.isHealthcareField;
    this.experience.healthcareField = form.healthcareField;
    this.experience.mayContactEmployer = form.mayContactEmployer;
  }

  updateExperienceForm(experience: IIHCManagementExperience): void {
    this.experienceForm.reset({
      company: experience.company,
      phone: experience.phone,
      address: experience.address,
      supervisor: experience.supervisor,
      jobTitle: experience.jobTitle,
      managementDuties: experience.managementDuties,
      isHealthcareField: experience.isHealthcareField,
      healthcareField: experience.healthcareField,
      mayContactEmployer: experience.mayContactEmployer
    });

    if(experience.from){
      this.experienceForm.get('from').setValue(new Date(experience.from));
    }
    if(experience.to){
      this.experienceForm.get('to').setValue(new Date(experience.to));
    }
  }

  saveExperience(): void {
    this.updateExperience();
    this.applicationService.updateManagementExperience(this.experience).subscribe({
      complete: () => {
        this.sharedService.toastr.success("Updated Your Management Experience");
        this.newExperienceCreated = false;
        this.toggleExperienceForm(false);
      },
      error: e => {
        this.sharedService.toastr.error("Could Not Save Experience");
        console.log('error updating management experience', e);
      }
    });
  }

  archiveExperience(experience: IIHCManagementExperience, archive: boolean): void {
    let archiving: string = archive? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this management experience entry?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.applicationService.archiveManagementExperience(experience.id, archive).subscribe({
            complete: () => {
              experience.archived = archive;
              if(archive){
                this.experiences = this.experiences.filter(e => e.id !== experience.id);
              }
              this.toggleExperienceForm(false);
            },
            error: e => {
              this.sharedService.toastr.error("Could Not Archive Experience");
              console.log('error archiving management experience', e);
            }
          });
        }
      }
    );
  }

  confirmDelete(experienceId: number): void {
    this.sharedService.openConfirm('Delete this management experience entry?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.deleteExperience(experienceId);
        }
      }
    );
  }

  deleteExperience(experienceId: number): void {
    this.applicationService.deleteManagementExperience(experienceId).subscribe({
      complete: () => {
        this.newExperienceCreated = false;
        this.experiences = this.experiences.filter(e => e.id !== experienceId);
        this.updateExperienceTable();
        this.experience = this.getEmptyExperience();
      },
      error: e => console.log('error deleting management experience', e)
    });
  }

  public get locked(): boolean {
    return !this.statuses.some(s => s.label === this.sharedService.started) && !this.isEditable;
  }

  public get approved(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.approved);
  }
}
